import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { WinAnimation } from '../components/WinAnimation';
import './CasinoBlackjack.css';

function getCardValue(c: number): number {
  if (c >= 10) return 10;
  return c + 1;
}

function handTotal(hand: number[]): number {
  let t = hand.reduce((s, c) => s + getCardValue(c), 0);
  if (hand.some(c => c === 0) && t <= 11) t += 10;
  return t;
}

export function CasinoBlackjack() {
  const { gameState, placeBet, addBalance, setLastWin, recordSpin, betOptions } = useGame();
  const [player, setPlayer] = useState<number[]>([]);
  const [dealer, setDealer] = useState<number[]>([]);
  const [phase, setPhase] = useState<'bet' | 'play' | 'result'>('bet');
  const [lastWin, setLastWinLocal] = useState(0);
  const [showWinAnim, setShowWinAnim] = useState(false);

  const draw = useCallback((): number => Math.floor(Math.random() * 13), []);

  const startHand = useCallback(() => {
    if (gameState.balance < gameState.bet) return;
    addBalance(-gameState.bet);
    const p = [draw(), draw()];
    const d = [draw(), draw()];
    setPlayer(p);
    setDealer(d);
    setPhase('play');
    if (handTotal(p) === 21) {
      setLastWin(gameState.bet * 2.5);
      setLastWinLocal(gameState.bet * 2.5);
      setShowWinAnim(true);
      recordSpin(true);
      setPhase('result');
    }
  }, [gameState.balance, gameState.bet, addBalance, setLastWin, recordSpin, draw]);

  const hit = useCallback(() => {
    const newCard = draw();
    setPlayer(prev => {
      const next = [...prev, newCard];
      const t = handTotal(next);
      if (t > 21) {
        recordSpin(false);
        setPhase('result');
      }
      return next;
    });
  }, [draw, recordSpin]);

  const stand = useCallback(() => {
    let dHand = [...dealer];
    while (handTotal(dHand) < 17) dHand = [...dHand, draw()];
    setDealer(dHand);
    const pt = handTotal(player);
    const dt = handTotal(dHand);
    let win = 0;
    if (dt > 21 || pt > dt) win = gameState.bet * 2;
    else if (pt === dt) win = gameState.bet;
    if (win > 0) {
      setLastWin(win);
      setLastWinLocal(win);
      setShowWinAnim(true);
      recordSpin(true);
    } else recordSpin(false);
    setPhase('result');
  }, [dealer, player, gameState.bet, setLastWin, recordSpin, draw]);

  const reset = useCallback(() => {
    setPlayer([]);
    setDealer([]);
    setPhase('bet');
  }, []);

  return (
    <div className="casino-blackjack">
      {showWinAnim && lastWin > 0 && (
        <WinAnimation amount={lastWin} isBonus={false} onEnd={() => setShowWinAnim(false)} />
      )}
      <div className="blackjack-container">
        <Link to="/" className="casino-back">← Takaisin</Link>
        <div className="casino-balance-bar">
          <span className="balance-value">€{gameState.balance}</span>
          {phase === 'bet' && (
            <select value={gameState.bet} onChange={(e) => placeBet(Number(e.target.value))} className="bet-select">
              {betOptions.map((b) => (
                <option key={b} value={b}>€{b % 1 ? b.toFixed(1) : b}</option>
              ))}
            </select>
          )}
        </div>
        <h1 className="blackjack-title">🃏 Blackjack</h1>
        {phase === 'bet' && (
          <button type="button" className="spin-button" onClick={startHand} disabled={gameState.balance < gameState.bet}>
            Jaa kädet €{gameState.bet}
          </button>
        )}
        {phase !== 'bet' && (
          <>
            <div className="hands">
              <div className="hand">
                <span>Pelaaja ({handTotal(player)})</span>
                <div className="cards">{player.map((c, i) => <span key={i} className="card">{c >= 10 ? ['J','Q','K','A'][c-10] : c + 1}</span>)}</div>
              </div>
              <div className="hand">
                <span>Jakaja ({phase === 'result' ? handTotal(dealer) : '?'})</span>
                <div className="cards">{dealer.map((c, i) => <span key={i} className="card">{i === 1 && phase === 'play' ? '?' : (c >= 10 ? ['J','Q','K','A'][c-10] : c + 1)}</span>)}</div>
              </div>
            </div>
            {phase === 'play' && (
              <div className="actions">
                <button type="button" className="dice-btn" onClick={hit}>Ota</button>
                <button type="button" className="dice-btn active" onClick={stand}>Pysy</button>
              </div>
            )}
            {phase === 'result' && (
              <button type="button" className="spin-button" onClick={reset}>Uusi peli</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
